import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { Html5Qrcode } from 'html5-qrcode';
import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import DialogTitle from 'src/common/components/DialogTitle';

const SCAN_DEBOUNCE_MS = 2000;

const scanRegionSx = {
  border: '2px dashed',
  borderColor: 'grey.300',
  borderRadius: 2,
  p: 3,
  textAlign: 'center',
  bgcolor: 'background.paper',
} as const;

interface Props {
  open: boolean;
  onClose: () => void;
  onScan: (registrationId: string) => void | Promise<void>;
}

export default function QrCheckinScannerDialog({ open, onClose, onScan }: Props) {
  const { t } = useTranslation();
  const reactId = useId();
  const elementId = `qr-checkin-scanner-${reactId.replace(/:/g, '')}`;
  const scannerRef = useRef<Html5Qrcode | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const onScanRef = useRef(onScan);
  const lastScanRef = useRef<{ id: string; at: number } | null>(null);
  const [manualCode, setManualCode] = useState('');
  const [cameraError, setCameraError] = useState(false);
  const [isScanning, setIsScanning] = useState(false);

  onScanRef.current = onScan;

  const handleDecoded = useCallback((decoded: string) => {
    const trimmed = decoded.trim();
    const now = Date.now();
    if (
      lastScanRef.current?.id === trimmed &&
      now - lastScanRef.current.at < SCAN_DEBOUNCE_MS
    ) {
      return;
    }
    lastScanRef.current = { id: trimmed, at: now };
    void onScanRef.current(trimmed);
  }, []);

  const stopScanner = useCallback(async () => {
    const active = scannerRef.current;
    scannerRef.current = null;
    if (!active) {
      setIsScanning(false);
      return;
    }
    try {
      if (active.isScanning) {
        await active.stop();
      }
      await active.clear();
    } catch {
      // ignore teardown errors
    }
    setIsScanning(false);
  }, []);

  useEffect(() => {
    if (!open) {
      setManualCode('');
      setCameraError(false);
      lastScanRef.current = null;
      void stopScanner();
    }
  }, [open, stopScanner]);

  useEffect(() => {
    return () => {
      void stopScanner();
    };
  }, [stopScanner]);

  const ensureScanner = useCallback(() => {
    if (!scannerRef.current) {
      scannerRef.current = new Html5Qrcode(elementId);
    }
    return scannerRef.current;
  }, [elementId]);

  const handleRequestCamera = async () => {
    setCameraError(false);
    setIsScanning(true);
    await new Promise<void>(resolve => {
      requestAnimationFrame(() => resolve());
    });

    try {
      const scanner = ensureScanner();
      await scanner.start(
        { facingMode: 'environment' },
        {
          fps: 10,
          qrbox: { width: 250, height: 250 },
          aspectRatio: 1,
        },
        handleDecoded,
        () => undefined,
      );
    } catch {
      setCameraError(true);
      setIsScanning(false);
      try {
        await scannerRef.current?.clear();
      } catch {
        // ignore
      }
      scannerRef.current = null;
    }
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) {
      return;
    }

    try {
      const scanner = ensureScanner();
      const decoded = await scanner.scanFile(file, false);
      handleDecoded(decoded);
    } catch {
      // Invalid or unreadable image; user can try another file or manual entry.
    }
  };

  const handleManualSubmit = () => {
    const trimmed = manualCode.trim();
    if (!trimmed) {
      return;
    }
    void onScan(trimmed);
  };

  const handleClose = () => {
    void stopScanner();
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle id="qr-checkin-scanner-title" onClose={handleClose}>
        {t('checkins.scanQrTitle')}
      </DialogTitle>
      <DialogContent>
        {cameraError && (
          <Typography color="error" variant="body2" sx={{ mb: 2 }}>
            {t('checkins.cameraPermissionDenied')}
          </Typography>
        )}
        <Box sx={scanRegionSx}>
          {!isScanning ? (
            <Stack spacing={1} sx={{ alignItems: 'center' }}>
              <QrCodeScannerIcon sx={{ fontSize: 48, color: 'grey.400', mb: 1 }} />
              <Typography variant="h6" gutterBottom>
                {t('checkins.scanAreaTitle')}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {t('checkins.scanAreaHint')}
              </Typography>
              <Button variant="outlined" onClick={() => void handleRequestCamera()} sx={{ mt: 1 }}>
                {t('checkins.requestCameraPermission')}
              </Button>
              <Link
                component="button"
                type="button"
                variant="body2"
                underline="hover"
                onClick={() => fileInputRef.current?.click()}
                sx={{ mt: 0.5 }}
              >
                {t('checkins.scanImageFile')}
              </Link>
            </Stack>
          ) : null}
          <Box
            id={elementId}
            sx={{
              display: isScanning ? 'block' : 'none',
              width: '100%',
              '& video': { borderRadius: 1 },
            }}
          />
        </Box>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          hidden
          onChange={e => void handleFileChange(e)}
        />
        <Stack direction="row" spacing={1} sx={{ mt: 2, alignItems: 'center' }}>
          <TextField
            fullWidth
            size="small"
            placeholder={t('checkins.manualCodeLabel')}
            value={manualCode}
            onChange={e => setManualCode(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                handleManualSubmit();
              }
            }}
          />
          <Button
            variant="outlined"
            size="small"
            onClick={handleManualSubmit}
            sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
          >
            {t('checkins.manualCodeSubmit')}
          </Button>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} variant="contained">
          {t('base.close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
